import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { Product, VendorProfile } from "types";
import TablePaginationControls from "components/TableandmobilePaginationcontrol";
import { DesktopTableParts, MobileCardsOnly } from "components/TableandMobilecomponent";
import { Row } from "@tanstack/react-table";
import { getImageSrc, usePresignedMap } from "components/Tableimageshelpers";
import { ReviewModal } from "components/ReviewModal";
import ConnectVendorModal from "components/ConnectVendorModal";
import VendorDetailsSlider from "components/VendorDetailsSlider";
import "../../assets/css/SignageOrders.css";
import {
  Star,
  BadgeCheck,
  ShieldCheck,
  Building2,
  CheckCircle2,
  Globe,
  Phone,
  Mail,
  MapPin,
  X,
  Check,
  Award,
  MessageSquare,
  DollarSign,
} from "lucide-react";
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { getAuthCookie } from "../../utils/authCookies";
interface FirmInfo {
  companyID: string;
  companyName: string;
  companyWebsite: string;
  address: Address;
  specialities: string;
  regionsServed: string;
}

interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  country: string;
  zip: string;
}

interface Supplier {
  id: string;
  companyID: string;
  companyName: string;
  licenseType: string;
  status: string;
  billingCycle: string;
  licenseValidity: string;
  firstName: string;
  lastName: string;
  userType: string;
  jobTitle: string;
  email: string;
  phone1: string;
  firmDetails: FirmInfo;
  modulePermissions: string[];
  created_at: string;
}

type Dimensions = {
  length: number;
  height: number;
  width: number;
  weight: number;
};

// type Product = {
//   product_id: string;
//   name: string;
//   product_type: string;
//   description: string;
//   brand: string;
//   category: string;
//   item_condition: string;
//   purchase_price: string;
//   retail_price: string;
//   package_dimensions: Dimensions;
//   // notes: string;
//   image: Image | null;
//   status: string;
// };

type Media = { url: string; alt_text?: string; default?: boolean };

type SupplierProduct = {
  product_id: string;
  signage_company_id: string;
  inventory_type: string;
  product: Product;
  product_type?: string;
  images: Media[];
  brand: string;
  available: number;
  product_name: string;
  category: string;
  price: number;

};

type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

type Category = {
  id: string;
  name: string;
  image: string;
  status: string;
};

function fuzzyTextFilter<TData>(
  row: Row<TData>,
  columnId: string,
  filterValue: string
) {
  const raw = row.getValue(columnId);
  const value = (typeof raw === "boolean"
    ? raw
      ? "Active"
      : "Inactive"
    : String(raw)
  ).toLowerCase();

  return value.includes(filterValue.toLowerCase());
}

const columnHelper = createColumnHelper<SupplierProduct>();

const AgentSupplierPage = () => {
  const { supplierId, categoryId, brandId } = useParams<{ supplierId: string, categoryId: string, brandId: string }>();
  const location = useLocation();
  const locationState: any = location.state;
  const brandNameFromState: string =
    locationState?.brandName ||
    locationState?.brand?.brand_name ||
    locationState?.brand?.name ||
    "";
  const categoryNameFromState: string = locationState?.categoryName || "";
  const profileIdForBrandsPage: string = localStorage.getItem("profileId") || "";
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const token = getAuthCookie("sessionToken");
  const [error, setError] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const itemsPerPage = 2;
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  // Draft typing value (does NOT trigger API); applied value triggers API fetch.
  const [draftGlobalFilter, setDraftGlobalFilter] = useState("");
  const [globalFilter, setGlobalFilter] = useState("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  // const [productobjects, setProductObjects] = useState<ProductObj[]>([]);
  const [supplierProducts, setSupplierProducts] = useState<SupplierProduct[]>(
    []
  );
  const [loading, setLoading] = useState(true);

  // Vendor details modal state
  const [showDetails, setShowDetails] = useState(false);
  const [vendorProfile, setVendorProfile] = useState<VendorProfile | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);

  // Review modal
  const [showReview, setShowReview] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState<string | null>(null);

  // Connect vendor modal
  const [showConnectModal, setShowConnectModal] = useState(false);

  localStorage.setItem("supplierId", supplierId || "");
  const navigate = useNavigate();
  const firstImageRaw = (images?: Array<{ url?: string; default?: boolean }>) => {
    if (!Array.isArray(images) || images.length === 0) return undefined;
    const def = images.find(m => m?.default) ?? images[0];
    return def?.url;               // could be 24-hex id or "uploads/.."
  };

  // supplierProducts is your array of product rows for the page/grid
  const firstImageList = React.useMemo(
    () => (supplierProducts || []).map(p => ({ imageId: firstImageRaw(p?.images) })),
    [supplierProducts]
  );

  // Presign only the first-image ids (collectCloudIds will auto-skip non-hex)
  const imageUrlMap = usePresignedMap(firstImageList as Array<{ imageId?: string }>, "imageId", token);

  // Presign logo for vendor profile - memoize to prevent infinite loop
  const logoList = useMemo(
    () => (vendorProfile ? [{ logo: vendorProfile.logo }] : []),
    [vendorProfile?.logo]
  );
  const logoUrlMap = usePresignedMap(logoList, "logo", token);

  /** Load vendor details (marketplace profile) */
  const loadVendorDetails = async () => {
    if (!supplierId || !token) return;

    try {
      setLoadingDetails(true);
      setDetailsError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/marketplace-profiles/created-by/${supplierId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch vendor details");
      }

      const data = await response.json();
      // API returns an array, take the first item
      const profile = Array.isArray(data) && data.length > 0 ? data[0] : null;

      if (profile) {
        setVendorProfile(profile);
        // Load reviews for this profile
        await loadVendorReviews(profile._id);
      } else {
        setDetailsError("No vendor profile found");
      }
    } catch (err) {
      setDetailsError(err instanceof Error ? err.message : "Failed to load vendor details");
    } finally {
      setLoadingDetails(false);
    }
  };

  /** Load vendor reviews */
  const loadVendorReviews = async (profileId: string) => {
    if (!profileId || !token) return;

    try {
      setLoadingReviews(true);
      setReviewsError(null);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/marketplace-reviews/profile/${profileId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to load reviews");
      }

      const data = await response.json();
      setReviews(Array.isArray(data) ? data : []);
    } catch (err) {
      setReviewsError(err instanceof Error ? err.message : "Failed to load reviews");
    } finally {
      setLoadingReviews(false);
    }
  };

  /** Open details modal */
  const openDetails = () => {
    setShowDetails(true);
    loadVendorDetails();
  };

  const closeDetails = () => setShowDetails(false);
  const openReview = () => setShowReview(true);
  const closeReview = () => setShowReview(false);

  /** Load vendor profile on mount for header logo/name */
  useEffect(() => {
    if (supplierId && token) loadVendorDetails();
  }, [supplierId, token]);

  // Fetch supplier data
  useEffect(() => {
    const fetchSupplierData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${supplierId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch teammember data");
        }
        const data = await response.json();
        if (data && data.firmDetails && data.firmDetails.address) {
          setSupplier(data);
        } else {
          console.error("Incomplete data", data);
        }
      } catch (error) {
        console.error("Error fetching teammember data:", error);
      }
    };

    if (supplierId) {
      fetchSupplierData();
    }
  }, [supplierId, token]);

  // Fetch products data
  // useEffect(() => {
  //   const fetchProducts = async () => {
  //     try {
  //       const response = await fetch(
  //         `${process.env.REACT_APP_API_URL}/signage/${supplierId}/inventory/products`,
  //         {
  //           method: "GET",
  //           headers: {
  //             Authorization: `Bearer ${token}`,
  //             "Content-Type": "application/json",
  //           },
  //         }
  //       );
  //       const data = await response.json();
  //       console.log(data);
  //       // setProducts(data);
  //       // setProductObjects(data);
  //       setSupplierProducts(data);
  //     } catch (err) {
  //       setError(
  //         err instanceof Error ? err.message : "An unknown error occurred"
  //       );
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchProducts();
  // }, [supplierId, token]);

  useEffect(() => {
    const fetchSupplierProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/signage/${supplierId}/marketplace/products/datatable`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              page: pagination.pageIndex + 1,
              limit: pagination.pageSize,
              search: globalFilter,
              filters: {
                // pass the chosen category id; send "" for All
                category_id: categoryId || "",
                brand_id: brandId || "",
              },
              sort: {
                field: "created_at",
                order: "desc",
              },
            }),
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch agents");
        }

        const result = await response.json();
        setRowCount(result.total);

        const supplierProducts = result?.data ?? [];
        if (Array.isArray(supplierProducts)) {
          // console.log("supplierProducts", supplierProducts);
          setSupplierProducts(supplierProducts);
        } else {
          throw new Error("Unexpected response format for agents");
        }

      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierProducts();
  }, [
    token,
    supplierId,
    globalFilter,
    pagination.pageIndex,
    pagination.pageSize,
    selectedCategoryId,
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/categories/created-by/${supplierId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        setCategories(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [token]);

  const columns = useMemo(() => [
    columnHelper.display({
      id: "serial",
      header: () => <div className="th-text S-No">S.No.</div>,
      cell: (info) => (
        <div className="text-sm S-No">
          {pagination.pageIndex * pagination.pageSize + info.row.index + 1}
        </div>
      ),
      size: 60,
    }),

    columnHelper.display({
      id: "image",
      header: () => <div className="th-text">Image</div>,
      cell: (info) => {
        const r = info.row.original;
        const raw = firstImageRaw(r?.images);          // product's first/default image
        const src = getImageSrc(raw, imageUrlMap);
        return src ? (
          <img
            src={src}
            alt={r.product_name}
            style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 8 }}
          />
        ) : raw ? (
          <div className="vd-noimg-sm">Loading…</div>
        ) : (
          <div className="vd-noimg-sm">No image</div>
        );
      },
      enableGlobalFilter: false,
      size: 80,
    }),

    columnHelper.accessor("product_name", {
      header: () => <div className="th-text">Product</div>,
      cell: (info) => {
        const r = info.row.original;
        return (
          <div>
            <div className="fw-600 wrap-anywhere1">{r.product_name}</div>
            <div className="d-flex gap-2 mt-1 flex-wrap">
              {r.brand && <span className="vd-chip vd-chip-blue">{r.brand}</span>}
              {r.category && <span className="vd-chip vd-chip-gray">{r.category}</span>}
            </div>
          </div>
        );
      },
      filterFn: fuzzyTextFilter,
      enableGlobalFilter: true,
      size: 320,
    }),

    columnHelper.accessor("price", {
      header: () => <div className="th-text">Price</div>,
      cell: (info) => {
        const v = info.getValue();
        return <div className="vd-price">{typeof v === "number" ? `$${v.toFixed(2)}` : "--"}</div>;
      },
      enableGlobalFilter: false,
      size: 110,
    }),

    columnHelper.accessor("available", {
      header: () => <div className="th-text">Available</div>,
      cell: (info) => {
        const v = info.getValue() ?? 0;
        const inStock = v > 0;
        return (
          <span className={`vd-chip ${inStock ? "vd-chip-green" : "vd-chip-gray"}`}>
            {inStock ? `${v} Available` : "Out of Stock"}
          </span>
        );
      },
      enableGlobalFilter: false,
      size: 170,
    }),

    // Hidden id for mobile card helper (if your MobileCardsOnly expects it)
    columnHelper.accessor("product_id", {
      id: "product_id",
      header: () => null,
      cell: () => null,
      enableGlobalFilter: false,
      size: 1,
    }),

    columnHelper.display({
      id: "actions",
      header: () => <div className="th-text">Actions</div>,
      cell: (info) => {
        const r = info.row.original;
        // console.log("Product ID:", r);
        // const productLink = p?.product_type === "variable"
        //           ? `/realtor/viewSignageVariableProduct/${supplierId}/${productobj.product_id}`
        //           : `/realtor/viewSignageProduct/${supplierId}/${productobj.product_id}`;
        return (
          // <Link to={productLink} className="btn btn-primary btn-sm rounded-pill px-3 vd-cta">
          //   <i className="ri-eye-line me-2" /> View Details
          // </Link>
          <h2></h2>
        );
      },
      enableGlobalFilter: false,
      size: 170,
    }),
  ], [pagination.pageIndex, pagination.pageSize, supplierId]);


  const table = useReactTable({
    data: supplierProducts,
    columns,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    state: {
      pagination,
      globalFilter,
    },
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
  });

  // console.log("Table rows",supplierProducts);



  type ProductCardGridProps = { data: SupplierProduct[] };

  const ProductCardGrid: React.FC<ProductCardGridProps> = ({ data }) => (
    <div className="row g-4">
      {data.map((p: any) => {
        // console.log("Rendering product", p);
        const raw = firstImageRaw(p.images);
        const src = getImageSrc(raw, imageUrlMap);

        const inStock = (p.available ?? 0) > 0;
        const productLink =
          (p?.product_type || p?.product?.product_type) == "variable"
            ? `/agent/viewSignageVariableProduct/${supplierId}/${p.product_id}`
            : `/agent/viewSignageProduct/${supplierId}/${p.product_id}`;
        return (
          <div className="col-md-6 col-lg-4 min-w-0" key={p.product_id}>
            <div className="vd-card shadow-border-2 box-shadow-datatables">
              <div className="vd-figure">
                {src ? (
                  <img src={src} alt={p.product_name} className="vd-img" />
                ) : (
                  <div className="vd-noimg">No image</div>
                )}
              </div>

              <div className="p-3 min-w-0">
                {/* <div className="d-flex gap-2 mb-2 flex-wrap">
                    {p.brand && <span className="vd-chip vd-chip-blue">{p.brand}</span>}
                    {p.category && <span className="vd-chip vd-chip-gray">{p.category}</span>}
                  </div> */}

                <div className="vd-card-title wrap-anywhere1">{p.product_name}</div>

                <div className="vd-card-meta-row d-flex align-items-center justify-content-between flex-wrap gap-2 mt-3">
                  <div className="vd-price">
                    {typeof p.price === "number" ? `$${p.price.toFixed(2)}` : "--"}
                  </div>
                  <span className={`vd-chip ${inStock ? "vd-chip-green" : "vd-chip-gray"}`}>
                    {inStock ? `${p.available} Available` : "Out of Stock"}
                  </span>
                </div>

                <Link to={productLink} className="vd-cta">
                  <i className="ri-eye-line me-2" /> View Details
                </Link>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const vendorName = vendorProfile?.vendor_name || supplier?.firmDetails?.companyName || "Vendor";
  const vendorLogoUrl = vendorProfile?.logo ? getImageSrc(vendorProfile.logo, logoUrlMap) : undefined;

  // Loading state must come after all hooks
  if (!supplier) return <div>Loading Teammember Data...</div>;
  return (
    <>
    <div className="overlay-page active" style={{ display: "block" }}>
      {/* <div className="overlay-page-header">
        <div className="overlay-page-header-content">
          <div className="header-left">
            <button type="button" onClick={() => navigate(-1)} className="back-btn" style={{ background: "none", border: "none", cursor: "pointer" }}>
              ←
            </button>
            <span className="page-title-text">Products</span>
          </div>
        </div>
      </div> */}

      <div className="overlay-page-content">
        <div className="vendor-info-card" id="catalogVendorCard">
          <div className="vendor-info-card-left">
            <button type="button" onClick={() => navigate(-1)} className="vendor-info-card-back" style={{ background: "none", border: "none", cursor: "pointer", textDecoration: "none" }}>
              ←
            </button>
            {/* <div
              className="vendor-info-card-logo"
              style={{
                background: vendorLogoUrl ? "transparent" : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: vendorLogoUrl ? "inherit" : "white",
              }}
            >
              {vendorLogoUrl ? (
                <img src={vendorLogoUrl} alt={vendorName} style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 10 }} />
              ) : (
                vendorName.charAt(0).toUpperCase()
              )}
            </div> */}
            <span className="vendor-info-card-name">{vendorName}</span>
          </div>
          <div className="vendor-info-card-right">
              <button type="button" className="btn-header-details" onClick={openDetails} style={{ marginLeft: 8, background: "var(--bg-light, #f5f5f5)", border: "1px solid var(--border)", borderRadius: 8, padding: "12px 20px", cursor: "pointer", fontWeight: 600 }}>
              Details
            </button>
            <button
              type="button"
              className="btn-connect-header"
              onClick={() => {
                if (supplierId) localStorage.setItem("supplierId", supplierId);
                setShowConnectModal(true);
              }}
            >
              + Connect
            </button>
          </div>
        </div>

        <div className="breadcrumb" id="catalogBreadcrumb">
          <span className="breadcrumb-item" onClick={() => navigate("/agent/suppliers")} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && navigate("/agent/suppliers")}>
            Vendors
          </span>
          <span className="breadcrumb-separator">›</span>
          <span
            className="breadcrumb-item"
            onClick={() => navigate("/agent/suppliers")}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate("/agent/suppliers")}
          >
            {vendorName}
          </span>
          <span className="breadcrumb-separator">›</span>
          <span
            className="breadcrumb-item"
            onClick={() => {
              if (profileIdForBrandsPage) {
                navigate(`/agent/suppliers/${supplierId}/brand/${profileIdForBrandsPage}`);
              } else {
                navigate(`/agent/suppliers/${supplierId}/category/${brandId}`);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key !== "Enter") return;
              if (profileIdForBrandsPage) {
                navigate(`/agent/suppliers/${supplierId}/brand/${profileIdForBrandsPage}`);
              } else {
                navigate(`/agent/suppliers/${supplierId}/category/${brandId}`);
              }
            }}
          >
            {brandNameFromState || "Brands"}
          </span>
          <span className="breadcrumb-separator">›</span>
          <span
            className="breadcrumb-item"
            onClick={() => navigate(`/agent/suppliers/${supplierId}/category/${brandId}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === "Enter" && navigate(`/agent/suppliers/${supplierId}/category/${brandId}`)}
          >
            {categoryNameFromState || "Categories"}
          </span>
          <span className="breadcrumb-separator">›</span>
          <span className="breadcrumb-item active">Products</span>
        </div>

        <div className="page-header-centered">
          <h1 className="page-title" id="catalogPageTitle">Available Products &amp; Services</h1>
          <p className="page-subtitle" id="catalogPageSubtitle">Browse and order from this vendor</p>
        </div>

        <div className="search-filter-card">
          <div className="search-filter-left">
            <span className="results-count" id="productsResultsCount">
              <strong>{rowCount}</strong> products
            </span>
          </div>
          <div className="search-filter-right">
            <div className="filter-search-input">
              <span className="search-icon">🔍</span>
              <input
                type="text"
                id="catalogSearchInput"
                placeholder="Search products..."
                value={draftGlobalFilter ?? ""}
                onChange={(e) => {
                  // typing should not trigger API calls
                  setDraftGlobalFilter(e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                    setGlobalFilter(draftGlobalFilter ?? "");
                  }
                }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="card1-grid">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card1">
                <div className="card1-header">
                  <div className="card1-logo" style={{ background: "#e0e0e0", color: "#999" }}>—</div>
                </div>
                <div className="card1-body">
                  <div className="card1-name">Loading...</div>
                  <div className="card1-description">—</div>
                  <div className="card1-stats">
                    <div className="card1-stat"><span className="card1-stat-label">Price</span><span className="card1-stat-value">—</span></div>
                    <div className="card1-stat"><span className="card1-stat-label">Stock</span><span className="card1-stat-value">—</span></div>
                  </div>
                  <div className="card1-footer">
                    <span className="btn-card-action" style={{ cursor: "default", opacity: 0.7 }}>View</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="card1-grid" id="catalogProductsGrid">
            {supplierProducts.map((p: SupplierProduct) => {
              // console.log("Rendering product card for", p);
              //  console.log("Product image src:",p?.product_type || p?.product?.product_type);
              //   console.log("Product image src2:",p?.product?.product_type);
              //    console.log("Product image src:",p?.product_type || p?.product?.product_type);
              const productLink =
                (p?.product_type || p?.product?.product_type) === "variable"
                  ? `/agent/viewSignageVariableProduct/${supplierId}/${p.product_id}`
                  : `/agent/viewSignageProduct/${supplierId}/${p.product_id}`;
              const raw = firstImageRaw(p.images);
              const src = getImageSrc(raw, imageUrlMap);
              const inStock = (p.available ?? 0) > 0;
              return (
                <div key={p.product_id} className="card1">
                  <div className="card1-header">
                    <div
                      className="card1-logo"
                      style={{
                        background: src ? "transparent" : "linear-gradient(135deg, #95E1D3 0%, #4ECDC4 100%)",
                        color: "white",
                      }}
                    >
                      {src ? (
                        <img src={src} alt={p.product_name} style={{ width: 190, height: 138, borderRadius: 10 }} />
                      ) : (
                        (p.product_name || "P").substring(0, 2).toUpperCase()
                      )}
                    </div>
                  </div>
                  <div className="card1-body" style={{ textAlign: "center" }}>
                    <div className="card1-name wrap-anywhere1">{p.product_name}</div>
                    <div className="card1-description">
                      {p.brand && <span className="me-2">{p.brand}</span>}
                      {p.category && <span>{p.category}</span>}
                      {!p.brand && !p.category && "—"}
                    </div>
                    <div className="card1-stats">
                      <div className="card1-stat">
                        <span className="card1-stat-label">Price</span>
                        <span className="card1-stat-value">{typeof p.price === "number" ? `$${p.price.toFixed(2)}` : "—"}</span>
                      </div>
                      <div className="card1-stat">
                        <span className="card1-stat-label">Stock</span>
                        <span className="card1-stat-value">{inStock ? `${p.available} Available` : "Out of Stock"}</span>
                      </div>
                    </div>
                    <div className="card1-footer">
                      <Link
                        to={productLink}
                        className="btn-card-action"
                        style={{ display: "block", textAlign: "center", textDecoration: "none", color: "white" }}
                      >
                        View Details →
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && supplierProducts.length === 0 && (
          <div className="card1-grid">
            <div className="card1" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "2rem" }}>
              No products found.
            </div>
          </div>
        )}

        {/* Pagination below cards */}
        {!loading && rowCount > 0 && (
          <div className="mt-4 mb-4">
            <TablePaginationControls table={table} totalItems={rowCount} />
          </div>
        )}
      </div>

      <ConnectVendorModal
        show={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        vendor={vendorName ? { id: supplierId || "", name: vendorName, logo: vendorLogoUrl } : null}
        userType="agent"
      />
    </div>

    <VendorDetailsSlider
      show={showDetails}
      onClose={closeDetails}
      profile={vendorProfile}
      logoUrlMap={logoUrlMap || {}}
      loading={loadingDetails}
      detailsError={detailsError}
      avgRating={reviews?.length ? reviews.reduce((s, r) => s + (r.rating ?? 0), 0) / reviews.length : undefined}
      reviewCount={reviews?.length ?? 0}
      reviews={reviews}
      loadingReviews={loadingReviews}
      reviewsError={reviewsError}
      onConnect={() => { if (supplierId) localStorage.setItem("supplierId", supplierId); setShowConnectModal(true); }}
      onWriteReview={openReview}
    />

    {/* REVIEW MODAL */}
    <ReviewModal
        show={showReview}
        vendor={
          vendorProfile
            ? { name: vendorProfile.vendor_name, id: vendorProfile._id, logo: vendorProfile.logo }
            : null
        }
        onClose={closeReview}
        stacked={showDetails}
        reviewType="vendor"
        profileId={vendorProfile?._id}
        logoUrlMap={logoUrlMap || {}}
        onSubmit={async () => {
          if (vendorProfile?._id) await loadVendorReviews(vendorProfile._id);
        }}
      />
    </>
  );

  // return (
  //   <div className="supplier-page">
  //     {/* Supplier Details Section */}
  //     <div className="row">
  //       <div className="col-12">
  //         <div className="page-title-box d-sm-flex align-items-center justify-content-between bg-galaxy-transparent position-relative">
  //           <h4 className="mb-sm-0">Supplier Details</h4>
  //           <div
  //             className="position-absolute"
  //             style={{ top: "16px", right: "16px" }}
  //           >
  //             {/* <Link
  //                         to={`/signage/edit-teammember/${teammember?.id}`}
  //                         className="text-decoration-none"
  //                       >
  //                         <i className="ri-pencil-fill fs-4 text-primary"></i>
  //                       </Link> */}
  //           </div>
  //           {/* <div className="page-title-right">
  //             <ol className="breadcrumb m-0">
  //               <li className="breadcrumb-item">
  //                 <Link to="#">Home</Link>
  //               </li>
  //               <li className="breadcrumb-item active">Supplier Details</li>
  //             </ol>
  //           </div> */}
  //         </div>
  //       </div>
  //     </div>

  //     {/* Supplier Information */}
  //     <div className="row">
  //       <div className="col-md-4">
  //         <div className="card">
  //           <div className="card-body p-4">
  //             <div>
  //               <div className="flex-shrink-0 avatar-md mx-auto">
  //                 <div className="avatar-title bg-light rounded">
  //                   <img
  //                     src="/assets/images/companies/img-1.png"
  //                     alt="Supplier Logo"
  //                     height="50"
  //                   />
  //                 </div>
  //               </div>
  //               <div className="mt-4 text-center">
  //                 <h5 className="mb-1">{supplier.firmDetails.companyName}</h5>
  //                 <p className="text-muted">{supplier.firmDetails.address.city}</p>
  //               </div>
  //               <div className="table-responsive">
  //                 <table className="table mb-0 table-borderless">
  //                   <tbody>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells"> Name</span></th>
  //                       <td className="table-cells"> {supplier.firstName} {supplier.lastName}</td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Job title / Role</span></th>
  //                       <td className="table-cells">{supplier.jobTitle}</td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Email</span></th>
  //                       <td className="table-cells">{supplier.email}</td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Website</span></th>
  //                       <td className="table-cells">
  //                         <Link to="#" className="link-primary">
  //                           {supplier.firmDetails.companyWebsite || "--"}
  //                         </Link>
  //                       </td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Contact No.</span></th>
  //                       <td className="table-cells">{supplier.phone1}</td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Fax</span></th>
  //                       <td className="table-cells">+1 999 876 5432</td>
  //                     </tr>
  //                     <tr>
  //                       <th><span className="fw-medium table-cells">Location</span></th>
  //                       <td className="">
  //                         {supplier.firmDetails.address.addressLine1}{" "}
  //                         {supplier.firmDetails.address.addressLine2}{" "}
  //                         {supplier.firmDetails.address.city}{" "}
  //                         {supplier.firmDetails.address.state}{" "}
  //                         {supplier.firmDetails.address.country}{" "}
  //                         {supplier.firmDetails.address.zip}
  //                       </td>
  //                     </tr>
  //                   </tbody>
  //                 </table>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //       <div className="col-md-8">
  //         <div className="row g-4 mb-3">
  //           <div className="col-sm-auto">
  //             <div>
  //               <h2 className="table-headers">Products</h2>
  //             </div>
  //           </div>
  //           <div className="col-sm">
  //             <div className="d-flex justify-content-sm-end">
  //               <div className="search-box ms-2">
  //                 <input
  //                   type="text"
  //                   className="form-control"
  //                   placeholder="Search Products..."
  //                 />
  //                 <i className="ri-search-line search-icon"></i>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //         <div className="card">
  //           <div className="card-body">
  //             <div
  //               id="table-product-list-all"
  //               className="table-card gridjs-border-none"
  //             >
  //               <table
  //                 id="transactions"
  //                 className="table  dt-responsive nowrap table-striped align-middle w-100"
  //               >
  //                 <thead>
  //                   <tr>
  //                     <th className="table-headers text-center" style={{ width: '6%' }}>S.No.</th>
  //                     {/* <th className="table-headers text-center" style={{ width: '10%' }}>Image</th> */}
  //                     <th className="table-headers text-center" style={{ width: '25%' }}>Product Name</th>
  //                     <th className="table-headers text-center" style={{ width: '6%' }}>SKU</th>
  //                     {/* <th className="table-headers text-center" style={{ width: '10%' }}>Category</th> */}
  //                     <th className="table-headers text-center" style={{ width: '6%' }}>Qty</th>
  //                     <th className="table-headers text-center" style={{ width: '7%' }}>Price</th>
  //                     <th className="table-headers text-center" style={{ width: '6%' }}>Status</th>

  //                     <th className="table-headers text-center" style={{ width: '6%' }}>Action</th>
  //                   </tr>
  //                 </thead>
  //                 <tbody>
  //                   {productobjects != null &&
  //                     productobjects?.map((productobj, index) => (
  //                       <tr key={index}>
  //                 <td className="text-center table-cells">{index + 1}</td>
  //                               <td className="text-start">
  //                   <div
  //                     className="d-flex align-items-center p-2"
  //                     style={{
  //                       // border: "1px solid #e5e5e5",
  //                       borderRadius: "8px",
  //                       // backgroundColor: "#fff",
  //                       minHeight: "70px",
  //                     }}
  //                   >
  //                     {/* ✅ Consistent Image Box */}
  //                     <div className="image-box"
  //                     >
  //                       {productobj?.product?.images?.[0]?.url ? (
  //                         <img
  //                           src={`${process.env.REACT_APP_BASE_URL}${productobj.product.images[0].url}`}
  //                           alt="Product"
  //                           style={{ width: "100%", height: "100%", objectFit: "cover" }}
  //                         />
  //                       ) : (
  //                         <span style={{ fontSize: "12px", color: "#888" }}>No image</span>
  //                       )}
  //                     </div>

  //                     {/* ✅ Product Info */}
  //                     <div>
  //                       {(productobj?.product?.product_type === "variable") ? (
  //                         <Link
  //                           to={`/agent/viewSignageVariableProduct/${supplierId}/${productobj.product_id}`}
  //                           className="fw-semibold text-dark d-block"
  //                           style={{ textDecoration: "none" }}
  //                         >
  //                           {productobj?.product?.name}
  //                         </Link>
  //                       ) : (
  //                         <Link
  //                           to={`/agent/viewSignageProduct/${supplierId}/${productobj.product_id}`}
  //                           className="fw-semibold text-dark d-block"
  //                           style={{ textDecoration: "none" }}
  //                         >
  //                           {productobj?.product?.name}
  //                         </Link>
  //                       )}

  //                       {/* ✅ Category */}
  //                     {productobj?.product?.category && (
  //                   <small className="text-muted">
  //                     Category : {productobj.product.category}
  //                   </small>
  //                 )}

  //                     </div>
  //                   </div>
  //                 </td>
  //                         <td className="table-cells text-center">{productobj?.product?.sku}</td>
  //                         {/* <td className="table-cells text-center">{productobj?.product?.category}</td> */}
  //                         <td className="table-cells text-center">{productobj?.product?.variants?.[0]?.inventory?.[0]?.available}</td>
  //                         <td className="table-cells text-center">{productobj?.product?.retail_price}</td>
  //                         <td className="table-cells text-center">

  //                           <input
  //                             type="radio"
  //                             className={
  //                               productobj?.product?.status
  //                                 ? "status-radio status-radio-active"
  //                                 : "status-radio status-radio-inactive"
  //                             }
  //                             checked
  //                             readOnly
  //                           />
  //                         </td>
  //                         {/* <td>{productobj?.product?.item_condition}</td>
  //                             <td>
  //                               {productobj?.product?.package_dimensions?.length} x{" "}
  //                               {productobj?.product?.package_dimensions?.height} x{" "}
  //                               {productobj?.product?.package_dimensions?.width}
  //                             </td>
  //                             <td>
  //                               {productobj?.product?.package_dimensions?.weight}
  //                             </td>   */}
  //                         <td className="table-cells text-center">
  //                           {(productobj?.product?.product_type === 'variable') ? (
  //                             <Link
  //                               to={`/agent/viewSignageVariableProduct/${supplierId}/${productobj.product_id}`}
  //                               className="border-0 p-0 view me-3"
  //                             >
  //                               <i className="ri-eye-fill fs-20"></i>{" "}
  //                             </Link>
  //                           ) : (
  //                             <Link
  //                               to={`/agent/viewSignageProduct/${supplierId}/${productobj.product_id}`}
  //                               className="border-0 p-0 view me-3"
  //                             >
  //                               <i className="ri-eye-fill fs-20"></i>{" "}
  //                             </Link>
  //                           )}
  //                         </td>
  //                       </tr>
  //                     ))}
  //                 </tbody>
  //               </table>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );
};

export default AgentSupplierPage;

